import { useState, useEffect, createContext, useContext, useCallback, useRef, useMemo } from 'react';
import { Platform, I18nManager } from 'react-native';
import * as Localization from 'expo-localization';
import * as Updates from 'expo-updates';
import { en, TranslationKey } from '@/texts/en';
import { fr } from '@/texts/fr';
import { es } from '@/texts/es';
import { it } from '@/texts/it';
import { ar } from '@/texts/ar';
import { am } from '@/texts/am';
import { de } from '@/texts/de';
import { ro } from '@/texts/ro';
import { supabase } from '@/lib/supabase';
import { userLanguagePreferenceCache } from '@/services/userLanguagePreferenceCache';
import { userPreferencesService } from '@/services/userPreferencesService';

const RTL_RELOAD_FLAG_KEY = 'rtl_reload_pending';

type Language = 'en' | 'fr' | 'es' | 'it' | 'ar' | 'am' | 'de' | 'ro';
type Translations = typeof en;

const translations: Record<Language, Translations> = {
  en,
  fr,
  es,
  it,
  ar,
  am,
  de,
  ro,
};

// Platform-specific storage functions
const getStorageItem = async (key: string): Promise<string | null> => {
  if (Platform.OS === 'web') {
    return localStorage.getItem(key);
  } else {
    const { getItemAsync } = await import('expo-secure-store');
    return await getItemAsync(key);
  }
};

const setStorageItem = async (key: string, value: string): Promise<void> => {
  if (Platform.OS === 'web') {
    localStorage.setItem(key, value);
  } else {
    const { setItemAsync } = await import('expo-secure-store');
    await setItemAsync(key, value);
  }
};

interface TranslationContextType {
  language: Language;
  userLanguage: Language | null;
  setLanguage: (lang: Language, smartuserId?: string | null) => Promise<void>;
  loadUserLanguagePreference: (userId: string) => Promise<void>;
  setCampaignRefetchCallback: (callback: () => Promise<void>) => void;
  t: (key: TranslationKey | string, params?: Record<string, string>) => string;
  isLoading: boolean;
  isInitialLoad: boolean;
  isLanguageStable: boolean;
  isRTL: boolean;
  textAlign: 'left' | 'right';
  flexDirection: 'row' | 'row-reverse';
}

const TranslationContext = createContext<TranslationContextType>({
  language: 'en',
  userLanguage: null,
  setLanguage: async () => {},
  loadUserLanguagePreference: async () => {},
  setCampaignRefetchCallback: () => {},
  t: (key: TranslationKey | string) => key as string,
  isLoading: true,
  isInitialLoad: true,
  isLanguageStable: false,
  isRTL: false,
  textAlign: 'left',
  flexDirection: 'row',
});

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

// Standalone function to get current locale outside of React components
export const TranslationProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<Language>('en');
  const [userLanguage, setUserLanguage] = useState<Language | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLanguageStable, setIsLanguageStable] = useState(false);
  const isInitialLoadRef = useRef(true);
  const userLanguageLoadedRef = useRef(false);
  const campaignRefetchCallbackRef = useRef<(() => Promise<void>) | null>(null);

  // Function to detect device/browser language
  const detectSystemLanguage = (): Language => {
    try {
      if (Platform.OS === 'web') {
        // For web, use browser language
        const browserLang = navigator.language || navigator.languages?.[0] || 'en';
        if (browserLang.startsWith('fr')) return 'fr';
        if (browserLang.startsWith('es')) return 'es';
        if (browserLang.startsWith('it')) return 'it';
        if (browserLang.startsWith('ar')) return 'ar';
        if (browserLang.startsWith('am')) return 'am';
        if (browserLang.startsWith('de')) return 'de';
        return 'en';
      } else {
        // For React Native, use device locale
        const deviceLocale = Localization.getLocales()[0]?.languageCode || 'en';
        if (deviceLocale.startsWith('fr')) return 'fr';
        if (deviceLocale.startsWith('es')) return 'es';
        if (deviceLocale.startsWith('it')) return 'it';
        if (deviceLocale.startsWith('ar')) return 'ar';
        if (deviceLocale.startsWith('am')) return 'am';
        if (deviceLocale.startsWith('de')) return 'de';
        return 'en';
      }
    } catch (error) {
      console.error('Error detecting system language:', error);
      return 'en';
    }
  };

  useEffect(() => {
    loadLanguage();
  }, []);

  const loadLanguage = async () => {
    const timestamp = new Date().toISOString();
    console.log(`🌍 [${timestamp}] [Bootstrap] Starting language resolution...`);

    try {
      const supportedLanguages: Language[] = ['en', 'fr', 'es', 'it', 'ar', 'am', 'de'];

      const cachedUserPref = await userLanguagePreferenceCache.preloadCachedLanguage();

      if (cachedUserPref && supportedLanguages.includes(cachedUserPref.language as Language)) {
        console.log(`🌍 [${timestamp}] [Bootstrap] Using cached user preference: ${cachedUserPref.language}`);
        setLanguageState(cachedUserPref.language as Language);
        setUserLanguage(cachedUserPref.language as Language);
        await setStorageItem('language', cachedUserPref.language);
        userLanguageLoadedRef.current = true;
        setIsLanguageStable(true);
        setIsLoading(false);
        isInitialLoadRef.current = false;
        return;
      }

      const savedLanguage = await getStorageItem('language');

      if (savedLanguage && supportedLanguages.includes(savedLanguage as Language)) {
        console.log(`🌍 [${timestamp}] [Bootstrap] Using stored language: ${savedLanguage}`);
        setLanguageState(savedLanguage as Language);
      } else {
        const systemLanguage = detectSystemLanguage();
        console.log(`🌍 [${timestamp}] [Bootstrap] Using system language: ${systemLanguage}`);
        setLanguageState(systemLanguage);
        await setStorageItem('language', systemLanguage);
      }

      setIsLanguageStable(true);
    } catch (error) {
      console.error(`🌍 [${timestamp}] [Bootstrap] Error loading language:`, error);
      const systemLanguage = detectSystemLanguage();
      setLanguageState(systemLanguage);
      setIsLanguageStable(true);
    } finally {
      setIsLoading(false);
      isInitialLoadRef.current = false;
    }
  };

  const setCampaignRefetchCallback = (callback: () => Promise<void>) => {
    campaignRefetchCallbackRef.current = callback;
  };

  const setLanguage = async (lang: Language, smartuserId?: string | null) => {
    const timestamp = new Date().toISOString();
    try {
      await setStorageItem('language', lang);
      setLanguageState(lang);
      setUserLanguage(lang);
      console.log(`🌍 [${timestamp}] ✅ Local storage and state updated to: ${lang}`);

      if (smartuserId) {
        await userLanguagePreferenceCache.cacheLanguage(smartuserId, lang);

        const updateSuccess = await userPreferencesService.updateInterfaceLanguage(smartuserId, lang);

        if (updateSuccess) {
          console.log(`🌍 [${timestamp}] ✅ Database and unified cache updated`);
        } else {
          console.error(`🌍 [${timestamp}] ❌ Error saving to database`);
        }
      } else {
        console.log(`🌍 [${timestamp}] No smartuserId, skipping database save`);
      }

      const { campaignCache } = await import('@/services/campaignCacheService');
      campaignCache.clear();
      const { persistentContentCache } = await import('@/services/persistentContentCacheService');
      await persistentContentCache.clear();
      if (campaignRefetchCallbackRef.current) {
        await campaignRefetchCallbackRef.current();
      }

      if (Platform.OS === 'web') {
        window.location.reload();
        return;
      }

    } catch (error) {
      console.error(`🌍 [${timestamp}] ❌ Error saving language:`, error);
    }
  };

  const loadUserLanguagePreference = useCallback(async (smartuserId: string) => {
    const timestamp = new Date().toISOString();
    const supportedLanguages: Language[] = ['en', 'fr', 'es', 'it', 'ar', 'am', 'de'];

    if (userLanguageLoadedRef.current) {
      console.log(`🌍 [${timestamp}] [UserPref] Already loaded from cache, validating...`);
    }

    try {
      const cachedInterfaceLang = userPreferencesService.getInterfaceLanguage(smartuserId);

      if (cachedInterfaceLang && supportedLanguages.includes(cachedInterfaceLang as Language)) {
        const userLang = cachedInterfaceLang as Language;
        console.log(`🌍 [${timestamp}] [UserPref] Using boot-scheduler cached preference: ${userLang}`);

        await userLanguagePreferenceCache.cacheLanguage(smartuserId, userLang);

        if (userLanguageLoadedRef.current && language === userLang) {
          console.log(`🌍 [${timestamp}] [UserPref] Cache matches current (${userLang}), no change needed`);
          return;
        }

        console.log(`🌍 [${timestamp}] [UserPref] Applying cached preference: ${userLang} (was: ${language})`);
        setUserLanguage(userLang);
        await setStorageItem('language', userLang);
        setLanguageState(userLang);
        userLanguageLoadedRef.current = true;
        return;
      }

      console.log(`🌍 [${timestamp}] [UserPref] No cached preference, querying database...`);
      const queryStartTime = Date.now();
      const { data, error } = await supabase
        .from('users')
        .select('interface_preferred_language')
        .eq('smartuser_id', smartuserId)
        .maybeSingle();
      const queryDuration = Date.now() - queryStartTime;

      if (error) {
        console.error(`🌍 [${timestamp}] ❌ Error loading user language preference (${queryDuration}ms):`, error);
        return;
      }

      if (data?.interface_preferred_language) {
        const userLang = data.interface_preferred_language as Language;

        if (supportedLanguages.includes(userLang)) {
          await userLanguagePreferenceCache.cacheLanguage(smartuserId, userLang);

          if (userLanguageLoadedRef.current && language === userLang) {
            console.log(`🌍 [${timestamp}] [UserPref] DB matches cache (${userLang}), no change needed`);
            return;
          }

          console.log(`🌍 [${timestamp}] [UserPref] Applying DB preference: ${userLang} (was: ${language})`);
          setUserLanguage(userLang);
          await setStorageItem('language', userLang);
          setLanguageState(userLang);
          userLanguageLoadedRef.current = true;
        } else {
          console.warn(`🌍 [${timestamp}] ⚠️ User language "${userLang}" is NOT supported`);
        }
      } else {
        console.log(`🌍 [${timestamp}] ⚠️ No user language preference in database`);
        await userLanguagePreferenceCache.clear();
      }
    } catch (error) {
      console.error(`🌍 [${timestamp}] ❌ Unexpected error loading user language preference:`, error);
    }
  }, [language]);

  const t = useCallback((key: TranslationKey | string, params?: Record<string, string>): string => {
    let translation: string | undefined;

    if (key.includes('.')) {
      const keys = key.split('.');
      let value: any = translations[language];
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          value = undefined;
          break;
        }
      }
      translation = typeof value === 'string' ? value : key;
    } else {
      translation = (translations[language] as any)[key] || key;
    }

    if (params) {
      Object.keys(params).forEach(paramKey => {
        translation = translation!.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), params[paramKey]);
      });
    }

    return translation ?? key;
  }, [language]);

  const isRTL = language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const flexDirection = isRTL ? 'row-reverse' : 'row';

  useEffect(() => {
    const applyRTL = async () => {
      const shouldBeRTL = language === 'ar';
      const timestamp = new Date().toISOString();

      if (Platform.OS === 'web') {
        document.documentElement.dir = shouldBeRTL ? 'rtl' : 'ltr';
        document.documentElement.lang = language;
      } else {
        const currentRTL = I18nManager.isRTL;
        console.log(`🌍 [${timestamp}] [RTL] Check: language=${language}, shouldBeRTL=${shouldBeRTL}, currentRTL=${currentRTL}`);

        if (currentRTL !== shouldBeRTL) {
          I18nManager.allowRTL(shouldBeRTL);
          I18nManager.forceRTL(shouldBeRTL);
          console.log(`🌍 [${timestamp}] [RTL] Direction changed to ${shouldBeRTL ? 'RTL' : 'LTR'} for language: ${language}`);

          const reloadFlagValue = await getStorageItem(RTL_RELOAD_FLAG_KEY);
          const hasReloadFlag = reloadFlagValue === 'true';

          if (hasReloadFlag) {
            console.log(`🌍 [${timestamp}] [RTL] Reload flag already set - clearing to prevent loop`);
            await setStorageItem(RTL_RELOAD_FLAG_KEY, 'false');
          } else {
            console.log(`🌍 [${timestamp}] [RTL] Mismatch detected! Setting flag and triggering reload...`);
            await setStorageItem(RTL_RELOAD_FLAG_KEY, 'true');

            setTimeout(async () => {
              try {
                console.log(`🌍 [${timestamp}] [RTL] Executing Updates.reloadAsync()...`);
                await Updates.reloadAsync();
              } catch (error) {
                console.error(`🌍 [${timestamp}] [RTL] Reload failed:`, error);
                await setStorageItem(RTL_RELOAD_FLAG_KEY, 'false');
              }
            }, 100);
          }
        } else {
          const reloadFlagValue = await getStorageItem(RTL_RELOAD_FLAG_KEY);
          if (reloadFlagValue === 'true') {
            console.log(`🌍 [${timestamp}] [RTL] RTL state is now correct - clearing reload flag`);
            await setStorageItem(RTL_RELOAD_FLAG_KEY, 'false');
          }
        }
      }
    };

    if (isLanguageStable) {
      applyRTL();
    }
  }, [language, isLanguageStable]);

  const value = useMemo<TranslationContextType>(() => ({
    language,
    userLanguage,
    setLanguage,
    loadUserLanguagePreference,
    setCampaignRefetchCallback,
    t,
    isLoading,
    isInitialLoad: isInitialLoadRef.current,
    isLanguageStable,
    isRTL,
    textAlign,
    flexDirection,
  }), [language, userLanguage, setLanguage, loadUserLanguagePreference, t, isLoading, isLanguageStable, isRTL]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};